// import angular modules
import { NgModule, forwardRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ReactiveFormsModule,
  FormsModule,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { RouterModule } from '@angular/router';
import { SuiModule } from '@richardlt/ng2-semantic-ui';
import { MomentModule } from 'ngx-moment';
import { FlexLayoutModule } from '@angular/flex-layout';
import { MentionModule } from '../mentions/public-api';
import { ContenteditableValueAccessorModule } from '@tinkoff/angular-contenteditable-accessor';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import {
  QuillEditorComponent,
  QuillModule,
  QuillViewHTMLComponent,
} from 'ngx-quill';
import { AppTabsModule } from '../tabs/tab.module';
import { ImageCropperModule } from 'ngx-img-cropper';
import { AdvancedSearchModalComponent } from './modals/advanced-search-modal/advanced-search-modal.component';
import { AdvancedSearchFilterComponent } from './components/advanced-search-filter/advanced-search-filter.component';
import { Quill } from 'quill';
import { EditorModule, TINYMCE_SCRIPT_SRC } from '@tinymce/tinymce-angular';
// import components
import {
  SearchFilterComponent,
  FieldErrorDisplayComponent,
  NoRecordFound,
  CommentComponent,
  CommentNestedComponent,
  InlineEditorComponent,
  WarningLabelComponent,
  MessageToastComponent,
  EslPillsComponent,
  EslEmptyComponent,
} from './components';
// import directives
import { IfOnDomDirective } from './directives/if-on-dom/if-on-dom.directive';
import { MarginDirective } from './directives/css-utils/margin/margin.directive';
import { PaddingDirective } from './directives/css-utils/padding/padding.directive';
import { TruncatePipe } from './pipes/truncate/truncate.pipe';
import { AppStatusDirective } from './directives/app-status/app-status.directive';
import {
  ClipboardDirective,
  PermissionDirective,
  BlockCopyPasteDirective,
  MyCurrencyFormatterDirective,
  RestrictToGreaterThanZeroDirective,
  MyWalletCurrencyFormatterDirective,
  ScrollNearEndDirective,
} from './directives';

//Modals
import {
  UploadDocumentModalComponent,
  EmailModalComponent,
  ConfirmModalComponent,
  RejectModalComponent,
  DynamicDataDetailModalComponent,
  ChatDocumentModalComponent,
  TwoFactorModalComponent,
  FieldCommentModalComponent,
  TwoFactorRecoveryModalComponent,
} from './modals';

//Pipes
import { FilterPipe } from './pipes/filter.pipe';
import { SortPipe } from './pipes/sort.pipe';
import { MyDateConverterPipe } from './pipes/date';
import {
  CreatedByPipe,
  CreatedByOrgPipe,
  DataRowValuePipe,
  MapPipe,
  ParentMetaPipe,
  RemoveUnderscorePipe,
  CreatedByAppPipe,
  PluralPipe,
  amountPipe,
} from './pipes';
const SHARED_COMPONENTS: Array<any> = [
  SearchFilterComponent,
  FieldErrorDisplayComponent,
  NoRecordFound,
  CommentComponent,
  CommentNestedComponent,
  InlineEditorComponent,
  WarningLabelComponent,
  MessageToastComponent,
  AdvancedSearchFilterComponent,
  EslButtonComponent,
  CurrencyfmtNumberInputComponent,
  ErrorComponentComponent,
  EslTagComponent,
  EslPillsComponent,
  EslEmptyComponent,
];

const SHARED_MODULES: Array<any> = [
  CommonModule,
  FormsModule,
  ReactiveFormsModule,
  FlexLayoutModule,
  SuiModule,
  AppTabsModule,
  // ImageViewerModule,
  // NouisliderModule,

  InfiniteScrollModule,
  MomentModule,
  ContenteditableValueAccessorModule,
  MentionModule,
  ImageCropperModule,
  EditorModule,
];

const SHARED_ENTRY_COMPONENTS: Array<any> = [
  ConfirmModalComponent,
  DynamicDataDetailModalComponent,
  EmailModalComponent,
  UploadDocumentModalComponent,
  ChatDocumentModalComponent,
  TwoFactorModalComponent,
  FieldCommentModalComponent,
  RejectModalComponent,
  AdvancedSearchModalComponent,
  TwoFactorRecoveryModalComponent,
];

const SHARED_PIPES: Array<any> = [
  DataRowValuePipe,
  TruncatePipe,
  FilterPipe,
  SortPipe,
  MyDateConverterPipe,
  CreatedByPipe,
  CreatedByOrgPipe,
  MapPipe,
  ParentMetaPipe,
  RemoveUnderscorePipe,
  CreatedByAppPipe,
  PluralPipe,
  amountPipe,
];

const SHARED_DIRECTIVES: Array<any> = [
  IfOnDomDirective,
  MarginDirective,
  PaddingDirective,
  AppStatusDirective,
  ClipboardDirective,
  PermissionDirective,
  BlockCopyPasteDirective,
  MyCurrencyFormatterDirective,
  RestrictToGreaterThanZeroDirective,
  MyWalletCurrencyFormatterDirective,
  ScrollNearEndDirective,
];
import { DecimalPipe } from '@angular/common';
import { PersonnelInfoModalComponent } from './modals/personnel-info-modal/personnel-info-modal.component';
import { EslButtonComponent } from './components/esl-button/esl-button.component';
import { CurrencyfmtNumberInputComponent } from './components/currencyfmt-number-input/currencyfmt-number-input.component';
import { ErrorComponentComponent } from './components/error-component/error-component.component';
import { EslTagComponent } from './components/esl-tag/esl-tag.component';
// import { WarningLabelComponent } from './components/warning-label/warning-label.component';
@NgModule({
  imports: [
    ...SHARED_MODULES,
    QuillModule.forRoot({
      modules: {
        // syntax: true,
        toolbar: [
          ['bold', 'italic', 'underline', 'strike'], // toggled buttons
          ['blockquote', 'code-block'],

          [{ header: 1 }, { header: 2 }], // custom button values
          [{ list: 'ordered' }, { list: 'bullet' }],
          [{ script: 'sub' }, { script: 'super' }], // superscript/subscript
          [{ indent: '-1' }, { indent: '+1' }], // outdent/indent
          [{ direction: 'rtl' }], // outdent/indent
          [{ size: ['small', false, 'large', 'huge'] }], // custom dropdown
          [{ header: [1, 2, 3, 4, 5, 6, false] }],

          [{ color: [] }, { background: [] }], // dropdown with defaults from theme
          [{ font: [] }],
          [
            { align: '' },
            { align: 'center' },
            { align: 'right' },
            { align: 'justify' },
          ],

          ['clean'], // remove formatting button

          ['link', 'image', 'video'], // link and image, video
        ],
      },
    }),
  ],
  declarations: [
    ...SHARED_DIRECTIVES,
    ...SHARED_COMPONENTS,
    ...SHARED_PIPES,
    DynamicDataDetailModalComponent,
    ...SHARED_ENTRY_COMPONENTS,
    PersonnelInfoModalComponent,
    CurrencyfmtNumberInputComponent,
    ErrorComponentComponent,
  ],
  providers: [
    { provide: TINYMCE_SCRIPT_SRC, useValue: 'tinymce/tinymce.min.js' },
    DecimalPipe,
  ],
  entryComponents: [...SHARED_ENTRY_COMPONENTS],
  exports: [
    ...SHARED_MODULES,
    ...SHARED_COMPONENTS,
    ...SHARED_DIRECTIVES,
    ...SHARED_PIPES,
    QuillModule,
  ],
})
export class SharedModule {}
